import { useState, useEffect } from 'react';
import api from './api';
import { FolderNode } from './FolderNode';

const STATUS_COLOR = {
  Planning:   { bg: 'var(--bg-subtle)', color: 'var(--text-dim)', border: 'var(--border-strong)' },
  Confirmed:  { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-text)' },
  Active:     { bg: 'var(--success-bg)', color: 'var(--success-text)', border: 'var(--success-text)' },
  'On Hold':  { bg: 'var(--warning-bg)', color: 'var(--warning-text)', border: 'var(--warning-text)' },
  Completed:  { bg: 'var(--purple-bg)', color: 'var(--purple-text)', border: 'var(--purple-text)' },
  Canceled:   { bg: 'var(--danger-bg)', color: 'var(--accent)', border: 'var(--accent)' },
};

const CONFIRMATION_CONFIG = {
  pending:   { label: '⏳ Awaiting your readiness confirmation', bg: 'var(--warning-bg)', color: 'var(--warning-text)' },
  confirmed: { label: '✓ Readiness confirmed', bg: 'var(--success-bg)', color: 'var(--success-text)' },
  declined:  { label: '✕ Declined', bg: 'var(--danger-bg)', color: 'var(--danger-text-soft)' },
};

// Відображений статус фрілансера завжди дзеркалить статус проєкту — з одним
// винятком: On Hold показується як Planning (рішення 03.08.2026).
const displayStatus = s => (s === 'On Hold' ? 'Planning' : s);

function StatusBadge({ status }) {
  const c = STATUS_COLOR[status] || STATUS_COLOR.Planning;
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      fontSize: '11px', fontWeight: 'bold', padding: '3px 10px',
      borderRadius: '6px', textTransform: 'uppercase', letterSpacing: '0.5px',
    }}>
      {status}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}


function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--bg-subtle)', fontSize: '13px' }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
      {children}
    </label>
  );
}

// Спільний вміст деталей проєкту — та сама розмітка, що й раніше жила лише
// в бічній панелі (quick view). Тепер використовується і там, і на
// повноекранній сторінці "View Full Details" (просто інший контейнер
// навколо, контент не дублюється).
function ProjectDetailBody({
  selected, confirmBusy, confirmError, setConfirmError, showDecline, setShowDecline,
  declineReason, setDeclineReason, handleConfirm, load,
}) {
  return (
    <>
      {/* Readiness confirmation — п.5: одна кнопка "Я готовий", не змінює
          відображений статус (той дзеркалить статус проєкту), лише
          позначає адміну, що фрілансер готовий. */}
      {selected.confirmationRequired && (
        <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning-text)', borderRadius: '8px', padding: '14px', marginBottom: '20px' }}>
          <p style={{ color: 'var(--warning-text)', fontSize: '13px', margin: '0 0 10px', fontWeight: 'bold' }}>
            This project is still in Planning — let us know you're ready.
          </p>
          <button disabled={confirmBusy} onClick={() => handleConfirm('confirmed')}
            style={{ background: 'var(--success-text)', color: '#fff', border: 'none', padding: '7px 16px', borderRadius: '6px', cursor: confirmBusy ? 'default' : 'pointer', fontSize: '13px', fontWeight: 'bold', opacity: confirmBusy ? 0.6 : 1 }}>
            {confirmBusy && !showDecline ? 'Saving…' : "✓ I'm ready"}
          </button>
          {confirmError && !showDecline && <p style={{ color: 'var(--danger-text-soft)', fontSize: '12px', margin: '8px 0 0' }}>{confirmError}</p>}
        </div>
      )}

      {/* Decline — окрема дія, не залежить від Planning/On Hold: фрілансер
          може відмовитись від проєкту в будь-який момент, поки він не
          завершений/скасований. */}
      {selected.confirmationStatus !== 'declined' && !['Completed', 'Canceled'].includes(selected.status) && (
        <div style={{ marginBottom: '20px' }}>
          {!showDecline ? (
            <button disabled={confirmBusy} onClick={() => setShowDecline(true)}
              style={{ background: 'transparent', color: 'var(--text-dim)', border: 'none', padding: 0, cursor: 'pointer', fontSize: '12px', textDecoration: 'underline' }}>
              Decline this project
            </button>
          ) : (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '14px' }}>
              <textarea
                value={declineReason}
                onChange={e => setDeclineReason(e.target.value)}
                placeholder="Reason for declining (required)"
                style={{ width: '100%', minHeight: '60px', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: '6px', padding: '8px', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <button disabled={confirmBusy} onClick={() => handleConfirm('declined')}
                  style={{ background: 'var(--danger-text-soft)', color: '#fff', border: 'none', padding: '7px 16px', borderRadius: '6px', cursor: confirmBusy ? 'default' : 'pointer', fontSize: '13px' }}>
                  {confirmBusy ? 'Saving…' : 'Submit decline'}
                </button>
                <button disabled={confirmBusy} onClick={() => { setShowDecline(false); setDeclineReason(''); setConfirmError(''); }}
                  style={{ background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--border-strong)', padding: '7px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                  Cancel
                </button>
              </div>
              {confirmError && <p style={{ color: 'var(--danger-text-soft)', fontSize: '12px', margin: '8px 0 0' }}>{confirmError}</p>}
            </div>
          )}
        </div>
      )}
      {selected.confirmationStatus === 'declined' && selected.declineReason && (
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-text-soft)', borderRadius: '8px', padding: '10px 14px', marginBottom: '20px' }}>
          <p style={{ color: 'var(--danger-text-soft)', fontSize: '12px', margin: 0 }}>Your reason: {selected.declineReason}</p>
        </div>
      )}

      {/* General information */}
      <DetailRow label="Company" value={selected.company || '—'} />
      <DetailRow label="Location" value={selected.location || '—'} />
      <DetailRow label="Project dates" value={`${formatDate(selected.startDate)} → ${formatDate(selected.endDate)}`} />
      {(selected.myPeriods || []).length > 1 ? (
        // Кілька проміжків участі (перерва в графіку) — кожен зі своїми
        // travel days, а не один суцільний діапазон.
        <div style={{ marginTop: '10px', marginBottom: '4px' }}>
          <SectionLabel>Your dates — {selected.myPeriods.length} periods</SectionLabel>
          {selected.myPeriods.map((p, i) => {
            const travelDates = Array.isArray(p.travelDates) ? p.travelDates : [];
            return (
              <div key={i} style={{ padding: '8px 0', borderBottom: i < selected.myPeriods.length - 1 ? '1px solid var(--bg-subtle)' : 'none' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{formatDate(p.dateFrom)} → {formatDate(p.dateTo)}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-disabled)', marginTop: '2px' }}>
                  {travelDates.length > 0
                    ? `✈️ Travel days: ${travelDates.map(formatDate).join(', ')}`
                    : 'No travel days marked for this period'}
                </div>
                {p.role && (
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>🎭 Role: {p.role}</div>
                )}
              </div>
            );
          })}
        </div>
      ) : (selected.myDateFrom || selected.myDateTo) && (
        <>
          <DetailRow label="Your dates" value={`${formatDate(selected.myDateFrom)} → ${formatDate(selected.myDateTo)}`} />
          <p style={{ color: 'var(--text-disabled)', fontSize: '11px', margin: '4px 0 0' }}>
            {(() => {
              const travelDates = selected.myPeriods?.[0]?.travelDates || [];
              return travelDates.length > 0
                ? `✈️ Travel days: ${travelDates.map(formatDate).join(', ')}`
                : 'No travel days marked for this period.';
            })()}
          </p>
        </>
      )}
      {(selected.myPeriods || []).length <= 1 && <DetailRow label="My role" value={selected.myRole || '—'} />}

      {selected.descriptionForFreelancers && (
        <div style={{ marginTop: '20px' }}>
          <SectionLabel>Description</SectionLabel>
          <p style={{ color: 'var(--text-body)', fontSize: '13px', lineHeight: '1.6', margin: 0 }}>{selected.descriptionForFreelancers}</p>
        </div>
      )}

      {(selected.assignedAdmins || []).length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <SectionLabel>Assigned admins</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {selected.assignedAdmins.map((a, i) => (
              <div key={i} style={{ color: 'var(--text-body)', fontSize: '13px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px', padding: '8px 12px' }}>
                👤 {a.name || 'Admin'}
              </div>
            ))}
          </div>
        </div>
      )}

      {(selected.responsiblePersons || []).length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <SectionLabel>Clients responsible persons</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {selected.responsiblePersons.map((p, i) => (
              <div key={i} style={{ color: 'var(--text-body)', fontSize: '13px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px', padding: '8px 12px' }}>
                <div style={{ fontWeight: 'bold' }}>{p.name || '—'}{p.role ? ` — ${p.role}` : ''}</div>
                {(p.phone || p.email) && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '2px' }}>
                    {[p.phone, p.email].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(selected.assignedFreelancers || []).length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <SectionLabel>Assigned freelancers</SectionLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {selected.assignedFreelancers.map((f, i) => (
              <div key={i} style={{ color: 'var(--text-body)', fontSize: '13px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '6px', padding: '8px 12px' }}>
                <div style={{ fontWeight: 'bold' }}>{f.name || '—'}{f.role ? ` — ${f.role}` : ''}</div>
                {(f.phone || f.whatsapp) && (
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px', marginTop: '2px' }}>
                    {[f.phone, f.whatsapp ? `WhatsApp: ${f.whatsapp}` : ''].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(selected.projectDocuments || []).length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <SectionLabel>Project documents</SectionLabel>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
            {selected.projectDocuments.map(f => (
              <FolderNode key={f.id} folder={f} projectId={selected.id} freelancerId={selected.freelancerId}
                area="project" isRoot myId={selected.freelancerId} onRefresh={load} />
            ))}
          </div>
        </div>
      )}

      {selected.myCrewFolder && (
        <div style={{ marginTop: '20px' }}>
          <SectionLabel>My files</SectionLabel>
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
            <FolderNode folder={selected.myCrewFolder} projectId={selected.id} freelancerId={selected.freelancerId}
              area="crew" isRoot myId={selected.freelancerId} onRefresh={load} />
          </div>
        </div>
      )}
    </>
  );
}

// ─── MyProjectsPage ─────────────────────────────────────────────────────────────
// Кабінет фрілансера: лише проєкти, куди призначений, без фінансової
// інформації (сервер сам відфільтровує income/outcome/finance).
export default function MyProjectsPage({ onBack }) {
  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [page, setPage] = useState('list'); // 'list' | 'detail'
  const [selectedId, setSelectedId] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const selected = projects.find(p => p.id === selectedId) || null;

  const load = () => {
    return api.projects.mine()
      .then(res => { setProjects(res.projects || []); })
      .catch(err => { setError(err.message || 'Failed to load projects'); });
  };

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, []);

  const closePanel = () => {
    setSelectedId(null);
    setShowDecline(false);
    setDeclineReason('');
    setConfirmError('');
  };

  const openDetails = () => setPage('detail');
  const backToList = () => {
    setPage('list');
    closePanel();
  };

  const handleConfirm = async (status) => {
    if (!selected) return;
    if (status === 'declined' && !declineReason.trim()) {
      setConfirmError('Please provide a reason for declining.');
      return;
    }
    setConfirmBusy(true);
    setConfirmError('');
    try {
      await api.projects.confirmMyAssignment(selected.id, status, status === 'declined' ? declineReason.trim() : undefined);
      await load();
      setShowDecline(false);
      setDeclineReason('');
    } catch (err) {
      setConfirmError(err.message || 'Failed to save your response');
    } finally {
      setConfirmBusy(false);
    }
  };

  // Повноекранний "View Full Details" — той самий контент, що й у бічній
  // панелі (ProjectDetailBody), лише інший, ширший контейнер навколо.
  if (page === 'detail' && selected) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={backToList}
              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
              ← Back
            </button>
            <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '18px' }}>{selected.name}</span>
            <StatusBadge status={displayStatus(selected.status)} />
            {selected.confirmationStatus && selected.confirmationStatus !== 'pending' && (
              <span style={{
                background: CONFIRMATION_CONFIG[selected.confirmationStatus].bg,
                color: CONFIRMATION_CONFIG[selected.confirmationStatus].color,
                fontSize: '11px', padding: '3px 8px', borderRadius: '5px',
              }}>
                {CONFIRMATION_CONFIG[selected.confirmationStatus].label}
              </span>
            )}
          </div>
        </div>
        <div style={{ padding: '28px 32px', maxWidth: '760px', margin: '0 auto' }}>
          <ProjectDetailBody
            selected={selected}
            confirmBusy={confirmBusy} confirmError={confirmError} setConfirmError={setConfirmError}
            showDecline={showDecline} setShowDecline={setShowDecline}
            declineReason={declineReason} setDeclineReason={setDeclineReason}
            handleConfirm={handleConfirm} load={load}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', fontFamily: 'Arial, sans-serif' }}>

      {/* Nav */}
      <div style={{ background: 'var(--bg-card)', padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onBack}
            style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            ← Back
          </button>
          <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '18px', letterSpacing: '2px' }}>MY PROJECTS</span>
          <span style={{ color: 'var(--text-dim)', fontSize: '13px' }}>{projects.length} assigned</span>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '28px 32px', maxWidth: '960px', margin: '0 auto' }}>
        {error && (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--accent)', color: 'var(--danger-text-soft)', padding: '10px 16px', borderRadius: '6px', fontSize: '13px', marginBottom: '20px' }}>
            ⚠️ {error}
          </div>
        )}

        {loading ? (
          <p style={{ color: 'var(--text-dim)', fontSize: '13px' }}>Loading…</p>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-dim)' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
            <p style={{ fontSize: '14px' }}>You're not assigned to any projects yet.</p>
            <p style={{ fontSize: '12px', color: 'var(--text-disabled)', marginTop: '4px' }}>Once the admin adds you to a project, it will show up here.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
            {projects.map(p => (
              <div
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                style={{
                  background: 'var(--bg-panel)', border: '1px solid var(--border)', borderRadius: '10px',
                  padding: '18px', cursor: 'pointer', transition: 'border-color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--text-disabled)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <h3 style={{ color: 'var(--text-primary)', fontSize: '15px', margin: 0, paddingRight: '8px' }}>{p.name}</h3>
                  <StatusBadge status={displayStatus(p.status)} />
                </div>
                {p.company && <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: '0 0 6px' }}>{p.company}</p>}
                {p.location && <p style={{ color: 'var(--text-faint)', fontSize: '12px', margin: '0 0 10px' }}>📍 {p.location}</p>}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', color: 'var(--text-dim)', borderTop: '1px solid var(--bg-subtle)', paddingTop: '10px' }}>
                  <span>{formatDate(p.startDate)} → {formatDate(p.endDate)}</span>
                  {p.myRole && (
                    <span style={{ background: 'var(--bg-card)', color: 'var(--text-body)', padding: '2px 8px', borderRadius: '4px' }}>{p.myRole}</span>
                  )}
                </div>
                {p.confirmationRequired && (
                  <div style={{ marginTop: '10px', background: CONFIRMATION_CONFIG.pending.bg, color: CONFIRMATION_CONFIG.pending.color, fontSize: '11px', padding: '4px 8px', borderRadius: '5px', textAlign: 'center' }}>
                    {CONFIRMATION_CONFIG.pending.label}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <div
          onClick={closePanel}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: '440px', maxWidth: '90vw', height: '100%', background: 'var(--bg-panel)', borderLeft: '1px solid var(--border)', padding: '28px', overflowY: 'auto' }}
          >
            <button onClick={closePanel}
              style={{ background: 'transparent', border: '1px solid var(--border-strong)', color: 'var(--text-muted)', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', marginBottom: '20px' }}>
              ✕ Close
            </button>
            <h2 style={{ color: 'var(--text-primary)', fontSize: '20px', margin: '0 0 8px' }}>{selected.name}</h2>
            <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <StatusBadge status={displayStatus(selected.status)} />
              {selected.confirmationStatus && selected.confirmationStatus !== 'pending' && (
                <span style={{
                  background: CONFIRMATION_CONFIG[selected.confirmationStatus].bg,
                  color: CONFIRMATION_CONFIG[selected.confirmationStatus].color,
                  fontSize: '11px', padding: '3px 8px', borderRadius: '5px',
                }}>
                  {CONFIRMATION_CONFIG[selected.confirmationStatus].label}
                </span>
              )}
            </div>

            <button onClick={openDetails}
              style={{ width: '100%', background: 'var(--bg-card)', border: '1px solid var(--accent)', color: 'var(--accent)', padding: '10px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', marginBottom: '20px' }}>
              View Full Details →
            </button>

            <ProjectDetailBody
              selected={selected}
              confirmBusy={confirmBusy} confirmError={confirmError} setConfirmError={setConfirmError}
              showDecline={showDecline} setShowDecline={setShowDecline}
              declineReason={declineReason} setDeclineReason={setDeclineReason}
              handleConfirm={handleConfirm} load={load}
            />
          </div>
        </div>
      )}
    </div>
  );
}
